<template>
  <div class="page">
    <div class="page-hero">
      <div>
        <h2>Puntos de Venta</h2>
        <p>Configuración de puntos de venta habilitados para facturación.</p>
      </div>
      <v-btn color="primary" small @click="openForm()">+ Nuevo Punto de Venta</v-btn>
    </div>

    <v-card outlined class="page-card">
      <v-card-text>
        <v-data-table :headers="headers" :items="rows" :loading="loading" dense>
          <template v-slot:item.isDefault="{ item }">
            <v-icon small color="success" v-if="item.isDefault">mdi-check-circle</v-icon>
            <span v-else>—</span>
          </template>
          <template v-slot:item.isCaeaContingency="{ item }">
            <v-chip x-small :color="item.isCaeaContingency ? 'warning' : 'grey'" dark label>
              {{ item.isCaeaContingency ? "Contingencia" : "—" }}
            </v-chip>
          </template>
          <template v-slot:item.isActive="{ item }">
            <v-chip x-small :color="item.isActive ? 'success' : 'grey'" dark label>
              {{ item.isActive ? "Activo" : "Inactivo" }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn text small color="primary" @click="openForm(item)">Editar</v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title>{{ form.id ? 'Editar' : 'Nuevo' }} Punto de Venta</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-text-field v-model="form.number" label="Número" type="number" outlined dense :rules="[v => !!v || 'Requerido']" />
            <v-text-field v-model="form.name" label="Nombre" outlined dense :rules="[v => !!v || 'Requerido']" />
            <v-select v-model="form.afipWs" :items="['wsfe','wsfex','wsbfe']" label="WS AFIP" outlined dense />
            <v-text-field v-model="form.address" label="Dirección" outlined dense />
            <v-switch v-model="form.isDefault" label="Default" inset />
            <v-switch v-model="form.isCaeaContingency" label="Punto exclusivo para CAEA (contingencia)" inset />
            <v-alert v-if="form.isCaeaContingency" type="warning" dense outlined>
              Este punto no puede ser predeterminado. Debe estar dado de alta en ARCA exclusivamente para CAEA.
            </v-alert>
            <v-switch v-model="form.isActive" label="Activo" inset />
          </v-form>
          <div v-if="error" class="error--text mt-2">{{ error }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { pointOfSaleService } from "../services/pointOfSale";

export default {
  name: "PointOfSaleView",
  data() {
    return {
      rows: [],
      loading: false,
      dialog: false,
      valid: false,
      saving: false,
      error: "",
      headers: [
        { text: "N°", value: "number" },
        { text: "Nombre", value: "name" },
        { text: "WS", value: "afipWs" },
        { text: "Default", value: "isDefault", align: "center" },
        { text: "CAEA", value: "isCaeaContingency", align: "center" },
        { text: "Estado", value: "isActive" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      form: { number: "", name: "", afipWs: "wsfe", address: "", isDefault: false, isCaeaContingency: false, isActive: true },
    };
  },
  created() {
    this.fetchRows();
  },
  methods: {
    unwrap(response) {
      if (response && response.data && response.data.data !== undefined) return response.data.data;
      return response && response.data ? response.data : [];
    },
    async fetchRows() {
      this.loading = true;
      try {
        const res = await pointOfSaleService.list();
        this.rows = this.unwrap(res) || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openForm(item) {
      this.error = "";
      if (item) {
        this.form = { ...item };
      } else {
        this.form = { number: "", name: "", afipWs: "wsfe", address: "", isDefault: false, isCaeaContingency: false, isActive: true };
      }
      this.dialog = true;
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      if (this.form.isCaeaContingency && this.form.isDefault) {
        this.error = "Un punto de venta CAEA no puede ser el predeterminado.";
        return;
      }
      this.saving = true;
      this.error = "";
      try {
        if (this.form.id) {
          await pointOfSaleService.update(this.form.id, this.form);
        } else {
          await pointOfSaleService.create(this.form);
        }
        this.dialog = false;
        await this.fetchRows();
      } catch (e) {
        this.error = e.response?.data?.error || e.message || "Error al guardar";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.page { padding: 0; }
.page-hero { background: var(--bg-card); border-radius: 16px; padding: 24px 28px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; gap: 12px; box-shadow: var(--shadow-md); border: 1px solid var(--border-subtle); }
.page-hero h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.02em; }
.page-hero p { margin: 0; color: var(--text-secondary); font-size: 14px; line-height: 1.5; }
.page-card { background: var(--bg-card); border-radius: 16px; padding: 8px 8px 4px; box-shadow: var(--shadow-md); border: 1px solid var(--border-subtle); }
</style>
